import { getExternalNodeName } from "@/workflow_api/types/workflow";
import type { RefName } from "@/workflow_api/types/core";
import {
  Avatar,
  AvatarFallback,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";

interface Props {
  node_type: RefName;
  node_name: string;
  status: string;
  status_label: string;
  flow_status?: string;
}

const FLOW_ERROR_STATUS = "FAILED";

const renderNode = new Set([
  "form-node",
  "revision-node",
  "payment-node",
  "sign-document-node",
  "confirm-associate-user-node",
  "resolution-node",
  "associate-user-node",
]);

type IconConfig = {
  name: string;
  type: string;
  color: "info" | "warning" | "action" | "success" | "danger" | "neutral";
};

const DEFAULT_ICON: IconConfig = {
  name: "arrows-rotate",
  type: "fas",
  color: "neutral",
};

const STATUS_ICON_MAP: Partial<Record<RefName, Record<string, IconConfig>>> = {
  "form-node": {
    PENDING: { name: "bars", type: "fas", color: "info" },
    EXTERNAL_FORM_PENDING_ENTRY: { name: "bars", type: "fas", color: "info" },
    IN_PROCESS: { name: "bars", type: "fas", color: "action" },
    EXTERNAL_FORM_RECEIVED: { name: "bars", type: "fas", color: "action" },
  },
  "revision-node": {
    PENDING: { name: "file-circle-info", type: "fas", color: "info" },
    UNDER_REVIEW: { name: "file-edit", type: "fas", color: "action" },
    IN_PROGRESS: { name: "file-edit", type: "fas", color: "action" },
    REVIEW_REVIEWING_CORRECTIONS: {
      name: "file-edit",
      type: "fas",
      color: "action",
    },
    IN_LEADER_REVIEW: { name: "clock", type: "far", color: "warning" },
    REVIEW_SENT_TO_REVIEW: { name: "clock", type: "far", color: "warning" },
    WITH_CORRECTIONS: { name: "send", type: "fas", color: "warning" },
    REVIEW_CORRECTION_IN_PROGRESS: {
      name: "file-edit",
      type: "fas",
      color: "action",
    },
    REVIEW_CORRECTIONS_RECEIVED: {
      name: "file-circle-check",
      type: "fas",
      color: "warning",
    },
    EXTENSION_REQUEST: { name: "timer", type: "fas", color: "warning" },
    SUSPENSION_REQUEST: { name: "circle-stop", type: "fas", color: "warning" },
    SUSPENDED: { name: "circle-stop", type: "fas", color: "neutral" },
    IN_PROCESS: { name: "file-edit", type: "fas", color: "action" },
    ON_EXTENSION: { name: "timer", type: "fas", color: "warning" },
  },
  "payment-node": {
    PAYMENT_PENDING: { name: "dollar", type: "fas", color: "info" },
  },
  "sign-document-node": {
    SIGNATURE_PENDING: { name: "signature", type: "fas", color: "info" },
    SIGNATURE_IN_PROGRESS: { name: "signature", type: "fas", color: "action" },
  },
  "resolution-node": {
    APPROVED: { name: "check", type: "fas", color: "success" },
    REJECTED: { name: "times", type: "fas", color: "danger" },
    CANCELED: { name: "minus", type: "fas", color: "neutral" },
  },
  "finish-node": {
    APPROVED: { name: "check", type: "fas", color: "success" },
    REJECTED: { name: "times", type: "fas", color: "danger" },
    CANCELED: { name: "minus", type: "fas", color: "neutral" },
    FORCE_COMPLETED: { name: "check", type: "fas", color: "success" },
  },
  "associate-user-node": {
    PENDING: { name: "clock", type: "fas", color: "info" },
    IN_PROCESS: { name: "arrows-rotate", type: "fas", color: "info" },
    RUNNING: { name: "arrows-rotate", type: "fas", color: "info" },
    SUBMITTED: { name: "check", type: "fas", color: "success" },
    COMPLETED: { name: "check", type: "fas", color: "success" },
  },
  "confirm-associate-user-node": {
    PENDING: { name: "clock", type: "fas", color: "info" },
    IN_PROCESS: { name: "arrows-rotate", type: "fas", color: "info" },
    RUNNING: { name: "arrows-rotate", type: "fas", color: "info" },
    SUBMITTED: { name: "check", type: "fas", color: "success" },
    COMPLETED: { name: "check", type: "fas", color: "success" },
  },
};

const statusIcon = (node_type: RefName, status: string): IconConfig =>
  STATUS_ICON_MAP[node_type]?.[status] ?? DEFAULT_ICON;

const RequestStatus = (props: Props) => {
  const iconProps = statusIcon(props.node_type, props.status);
  const isRenderNode = renderNode.has(props.node_type);

  return (
    <div className="flex gap-2 items-center">
      {props.flow_status === FLOW_ERROR_STATUS && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="flex items-center"
              aria-label="Error en la ejecución del flujo"
            >
              <Icon
                type="fas"
                name="triangle-exclamation"
                className="text-warning-quiet text-sm"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-130">
            <p className="text-sm text-body-quiet text-wrap">
              Error en la ejecución del flujo
            </p>
          </TooltipContent>
        </Tooltip>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Avatar size="2xs">
            <AvatarFallback
              color={iconProps?.color || "default"}
              type="icon"
              className="flex items-center justify-center"
            >
              <Icon
                type={iconProps?.type || "fas"}
                name={iconProps?.name || "file-circle-info"}
                className="text-sm"
              />
            </AvatarFallback>
          </Avatar>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-130">
          <p className="text-sm font-semibold text-heading-quiet text-wrap">
            Paso:{" "}
            <span className="text-body-quiet font-normal">
              {getExternalNodeName(props.node_type)}
            </span>
          </p>
          <p className="text-sm font-semibold text-heading-quiet text-wrap">
            Nombre:{" "}
            <span className="text-body-quiet font-normal">
              {props.node_name}
            </span>
          </p>
        </TooltipContent>
      </Tooltip>

      <div className="flex flex-col gap-1 max-w-37.5">
        <p className="text-body font-normal truncate">
          {isRenderNode ? props.node_name : "Acciones del sistema"}
        </p>
        <p className="text-body-quiet text-xs font-normal truncate">
          {props.status_label}
        </p>
      </div>
    </div>
  );
};

export default RequestStatus;
